import { useEffect, useState } from "react";

import { NODE_STATUS } from "@doitintl/cmp-models";
import { CloudFlowNodeType, type ReferencedNodeValue } from "@doitintl/cmp-models";
import { Stack, Typography } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import * as Yup from "yup";

import { cloudflowTexts } from "../../../../assets/texts";
import { ReferencedFieldStandalone } from "../ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldStandalone";
import { type NodeWitOutputModel } from "../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { createReferencedNodeSchema } from "../ApiActionParametersForm/useApiActionParametersSchema";
import { getFieldRef } from "../Common/utils";
import { useNodeConfigurationContext } from "../ConfigurationPanel/NodeConfigurationContext";

type FieldReferenceFormProps = {
  referenceableNodes: NodeWitOutputModel[];
  confirmSwitchDataSource: (
    onConfirm: (() => Promise<void>) | undefined,
    onClose: (() => Promise<void>) | undefined
  ) => Promise<void>;
  selectedNode: NodeWitOutputModel | undefined;
  setSelectedNode: (node: NodeWitOutputModel) => void;
  texts: {
    referenceField: {
      title: string;
      subtitle: string;
    };
  };
};

const FieldReferenceForm = ({
  referenceableNodes,
  confirmSwitchDataSource,
  setSelectedNode,
  selectedNode,
  texts: {
    referenceField: { title: referenceFieldTitle, subtitle: referenceFieldSubtitle },
  },
}: FieldReferenceFormProps) => {
  const { nodeConfig, updateNode } = useNodeConfigurationContext<
    CloudFlowNodeType.FILTER | CloudFlowNodeType.CONDITION
  >();
  const fieldReference = getFieldRef(nodeConfig);
  const conditionGroups = nodeConfig.parameters.conditionGroups;

  const [errors, setErrors] = useState<{ dataSource?: string; fieldReference?: string }>({});
  const [touched, setTouched] = useState<{ dataSource?: boolean; fieldReference?: boolean }>({});

  const updateNodeErrors = (prevErrors: Partial<Record<string, string>> | undefined, isValid: boolean) => {
    const errors = { ...prevErrors };

    if (isValid) {
      delete errors.param_error;
    } else {
      errors.param_error = cloudflowTexts.STATUS.REUIRED_FIELDS_ERROR;
    }

    return { errors };
  };

  useEffect(() => {
    setErrors({});
    setTouched({});
  }, [nodeConfig.id]);

  const validationSchema = Yup.object({
    dataSource: Yup.object().required(cloudflowTexts.DATA_SOURCE_REQUIRED),
    fieldReference: createReferencedNodeSchema(referenceableNodes).label("Field"),
  });

  const validate = (values) => {
    try {
      validationSchema.validateSync(values, { abortEarly: false });
      setErrors({});
      return {};
    } catch (err) {
      const validationErrors: { dataSource?: string; fieldReference?: string } = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path as keyof typeof validationErrors] = error.message;
          }
        });
      }
      setErrors(validationErrors);

      return validationErrors;
    }
  };

  const handleFieldReferenceChange = async (event: { target: { name: string; value: ReferencedNodeValue } }) => {
    const newFieldReference = {
      referencedNodeId: event.target.value?.referencedNodeId || "",
      referencedField: event.target.value?.referencedField || [],
    };
    const nodeId = newFieldReference.referencedNodeId;
    const node = referenceableNodes.find((node) => node.id === nodeId);

    const onConfirm = async () => {
      if (node) {
        setSelectedNode(node);
      }
      setTouched((prev) => ({ ...prev, fieldReference: true }));

      const validationErrors = validate({ dataSource: node, fieldReference: newFieldReference });
      const isValid = isEmpty(validationErrors);
      const statusMessage = isValid ? "" : Object.values(validationErrors).join(", ");

      const prefix = nodeConfig.type === CloudFlowNodeType.CONDITION ? "IF statement" : "Filter";
      const hasUpdatedName = nodeConfig.name !== `${prefix} on ${selectedNode?.name}` && nodeConfig.name !== prefix;

      updateNode((prevNode) => ({
        ...(!hasUpdatedName && node && { name: `${prefix} on ${node.name}` }),
        parameters: {
          ...prevNode.parameters!,
          ...newFieldReference,
          ...(!node && { conditionGroups: [] }),
        },
        statusMessage,
        status: isValid ? NODE_STATUS.VALIDATED : NODE_STATUS.ERROR,
        ...updateNodeErrors(prevNode.errors, isValid),
      }));
    };

    const sameDataSource =
      conditionGroups.every((group) => group.conditions.length === 0) || !selectedNode || selectedNode?.id === nodeId;
    if (sameDataSource) {
      await onConfirm();
    } else {
      await confirmSwitchDataSource(onConfirm, undefined);
    }
  };

  const handleBlur = (field: keyof typeof touched) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const validationErrors = validate({ dataSource: selectedNode, fieldReference });

    const isValid = isEmpty(validationErrors);
    // TODO: rian what should we do with this validation info in status message

    // const statusMessage = isValid ? "" : Object.values(validationErrors).join(", ");
    updateNode((prevNode) => ({
      status: isValid ? NODE_STATUS.VALIDATED : NODE_STATUS.ERROR,
      ...updateNodeErrors(prevNode.errors, isValid),
    }));
  };

  return (
    <Stack
      sx={{
        mb: 1,
      }}
    >
      <Stack
        sx={{
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          {referenceFieldTitle}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {referenceFieldSubtitle}
        </Typography>
      </Stack>
      <ReferencedFieldStandalone
        referenceableNodes={referenceableNodes}
        value={fieldReference}
        name="fieldReference"
        label={cloudflowTexts.FIELD}
        onChange={(e) => void handleFieldReferenceChange(e)}
        onBlur={handleBlur("fieldReference")}
        required
        helperText={touched.fieldReference && errors.fieldReference ? errors.fieldReference : ""}
        error={!!(touched.fieldReference && errors.fieldReference)}
      />
    </Stack>
  );
};

export default FieldReferenceForm;
